import React from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogTitle, DialogContent, IconButton, Button, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Tabs, Tab, Box } from '@mui/material';

const TAB_LABELS = ["Summary", "Journal", "Reserved 1", "Reserved 2"];

/** Tab positions, exported so callers can ask for one by name rather than by number. */
export const SUMMARY_TAB_INDEX = 0;
export const JOURNAL_TAB_INDEX = 1;

const MessagesPanelDialog = ({ open, onClose, tabContents = [], studyTitle,
  journalMarkdown, onJournalChange, onJournalReset,
  journalEditing: journalEditingProp, onJournalEditingChange,
  tabIndex: tabIndexProp, onTabIndexChange }) => {
  const [localTabIndex, setLocalTabIndex] = React.useState(SUMMARY_TAB_INDEX);

  // Which tab is showing is controllable from above, the same way edit mode is, because a study
  // that writes to the Journal needs to be able to put the reader in front of it. Opening on
  // Summary after a study that only fills the Journal shows a blank panel, which reads as the
  // study having produced nothing.
  //
  // Falls back to local state when no controller is supplied, so the component still works alone.
  const tabIsControlled = typeof onTabIndexChange === 'function';
  const tabIndex = tabIsControlled ? (tabIndexProp || SUMMARY_TAB_INDEX) : localTabIndex;
  const setTabIndex = (next) => {
    if (tabIsControlled) onTabIndexChange(next); else setLocalTabIndex(next);
  };

  // Edit mode is controllable from above so the owner of the journal can tell when the user is
  // typing. The trendline summary rewrites its own section whenever a line is drawn or cleared,
  // and doing that under an open editor would overwrite what is being typed mid-keystroke.
  // Falls back to local state when no controller is supplied, so the component still works alone.
  const [localEditing, setLocalEditing] = React.useState(false);
  const isControlled = typeof onJournalEditingChange === 'function';
  const journalEditing = isControlled ? !!journalEditingProp : localEditing;
  const setJournalEditing = (next) => {
    const value = typeof next === 'function' ? next(journalEditing) : next;
    if (isControlled) onJournalEditingChange(value); else setLocalEditing(value);
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  /**
   * Keeps the newest journal entry in view.
   *
   * `appendJournalEntry` adds to the END of the markdown, and the view pane is a fixed-height
   * scroller, so after a handful of studies a new entry lands below the fold. The panel opened
   * showing the oldest entries and the study you just ran was invisible — which reads as the
   * study having logged nothing.
   *
   * Runs on the markdown changing, on the tab becoming visible, and on leaving edit mode, because
   * in the last two cases the node has only just been mounted and has no scroll position yet.
   *
   * Deliberately NOT while editing: the textarea is where the customer types, and dragging it to
   * the bottom on every keystroke would fight them. The dependency list still includes
   * `journalEditing` so that switching back to view mode scrolls to the newest entry.
   */
  const journalViewRef = React.useRef(null);

  /**
   * Attaching the node and scrolling it are the same moment.
   *
   * A plain ref plus an effect does not work here: this panel is a MUI Dialog, which renders its
   * children through a portal that is committed AFTER the effects of the component that owns it.
   * On the first open `journalViewRef.current` is still null when the effect runs, so the panel
   * opened at the top exactly once — the one time it matters most, since that is when the study
   * that prompted it has just been logged.
   *
   * A callback ref fires when the DOM node actually attaches, which is the event we want. It also
   * covers the tab and edit-mode cases for free: in either the scroller is not rendered at all, so
   * the callback simply never runs.
   */
  const attachJournalView = React.useCallback((node) => {
    journalViewRef.current = node;
    if (node) node.scrollTop = node.scrollHeight;
  }, []);

  // And this handles the case the callback ref cannot see: the node is already mounted and new
  // text arrives underneath it.
  React.useEffect(() => {
    if (!open || tabIndex !== JOURNAL_TAB_INDEX || journalEditing) return undefined;
    const scroll = () => {
      const node = journalViewRef.current;
      if (node) node.scrollTop = node.scrollHeight;
    };
    scroll();
    // Re-run after paint as well: scrollHeight only reflects the new text once it has been laid
    // out. The synchronous call is what makes this observable under jsdom, which never paints.
    const raf = typeof window !== 'undefined' && window.requestAnimationFrame
      ? window.requestAnimationFrame(scroll)
      : null;
    return () => { if (raf && window.cancelAnimationFrame) window.cancelAnimationFrame(raf); };
  }, [journalMarkdown, open, tabIndex, journalEditing]);

  const handleDownloadJournal = () => {
    const blob = new Blob([journalMarkdown || ''], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `study-journal-${new Date().toISOString().slice(0, 10)}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const renderJournalTab = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <Tooltip title={journalEditing ? 'Switch to view mode' : 'Edit the journal'}>
          <Button size="small" variant="outlined" onClick={() => setJournalEditing(e => !e)}>
            {journalEditing ? 'View' : 'Edit'}
          </Button>
        </Tooltip>
        <Button size="small" variant="outlined" onClick={handleDownloadJournal}>Download .md</Button>
        <Button size="small" variant="outlined" color="error" onClick={onJournalReset}>Reset</Button>
      </Box>
      {journalEditing ? (
        <textarea
          value={journalMarkdown || ''}
          onChange={e => typeof onJournalChange === 'function' && onJournalChange(e.target.value)}
          style={{
            width: '100%', flex: 1, minHeight: 260, fontFamily: 'monospace',
            fontSize: 14, padding: 8, border: '1px solid #ccc', borderRadius: 4,
            resize: 'vertical', whiteSpace: 'pre-wrap',
          }}
        />
      ) : (
        <Box ref={attachJournalView} data-testid="journal-view" sx={{
          whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 14,
          bgcolor: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 1,
          p: 1.5, overflowY: 'auto', minHeight: 260, maxHeight: '55vh',
        }}>
          {journalMarkdown || 'No journal entries yet. Run a study to auto-log results here.'}
        </Box>
      )}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      hideBackdrop
      disableEnforceFocus
      PaperProps={{
        elevation: 12,
        sx: {
          position: 'fixed',
          top: 64,
          right: 48,
          minWidth: 560,
          minHeight: 340,
          maxWidth: '46vw',
          maxHeight: '80vh',
          zIndex: 1400,
          boxShadow: 16,
          borderRadius: 3,
          backgroundColor: 'background.paper',
        }
      }}
    >
      <DialogTitle
        sx={{
          pr: 6,
          fontWeight: 'bold',
          fontSize: 22,
          letterSpacing: 0.5,
          color: 'primary.main',
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.default',
        }}
      >
        {studyTitle ? `Message Panel - ${studyTitle}` : 'Message Panel'}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 12,
            top: 12,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        {TAB_LABELS.map((label, index) => (
          <Tab key={label} label={label} disabled={index > 1} />
        ))}
      </Tabs>
      <DialogContent dividers>
        <Box sx={{ minHeight: 120 }}>
          {tabIndex === 1 ? renderJournalTab() : (
            tabContents[tabIndex] ??
            <div style={{ color: '#666' }}>
              No content for this tab.
            </div>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

MessagesPanelDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  tabContents: PropTypes.arrayOf(PropTypes.node),
  studyTitle: PropTypes.string,
  journalMarkdown: PropTypes.string,
  onJournalChange: PropTypes.func,
  onJournalReset: PropTypes.func,
  // Optional: when supplied, edit mode is controlled by the owner of the journal so it can avoid
  // rewriting the text while the user is typing. Omitted, the component manages it locally.
  journalEditing: PropTypes.bool,
  onJournalEditingChange: PropTypes.func,
  // Optional: when supplied, the visible tab is controlled from above, so a study can open the
  // panel on the tab it actually wrote to. Omitted, the component manages it locally.
  tabIndex: PropTypes.number,
  onTabIndexChange: PropTypes.func,
};

export default MessagesPanelDialog;